import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";
import { LxmAppModule, UserAction } from "../enum";
import moment from "moment-timezone";
import { environment } from "src/environments/environment";
import { IUserSettings } from "../models";
import { Guid } from "../util/Guid";
import { HttpBadRequestResponse } from "../_helpers";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { LOCAL_STORAGE_KEY } from "../config";

@Injectable({ providedIn: "root" })
export class AuthenticationService implements OnDestroy {
  private readonly _jwt: BehaviorSubject<IToken>;
  private readonly _tokenString: BehaviorSubject<string>;
  private readonly _currentTenantId: BehaviorSubject<string>;
  private readonly _tenantTimeZone: BehaviorSubject<string>;
  private readonly _;

  private _lastError: HttpBadRequestResponse;

  public shouldLogOutSubscription: Subscription;

  private _subscriptions: Subscription[] = [];
  private _sessionTimeout: any;
  private _storageListener: (event: StorageEvent) => void;
  private _beforeUnloadListener: () => void;

  public get lastError() {
    const tmp = this._lastError;
    this._lastError = undefined;
    return tmp;
  }

  constructor(
    private _http: HttpClient,
    private _router: Router,
    private _dialog: MatDialog
  ) {
    this._tokenString = new BehaviorSubject<string>(null);
    this._currentTenantId = new BehaviorSubject<string>(null);
    this._tenantTimeZone = new BehaviorSubject<string>(null);
    this._jwt = new BehaviorSubject<IToken>(null);

    this._tokenString.next(localStorage.getItem(LOCAL_STORAGE_KEY.JWT));

    this._subscriptions.push(
      this._tokenString.subscribe((token) => {
        localStorage.setItem(LOCAL_STORAGE_KEY.JWT, token);

        const jwt = token ? this._parseJwt(token) : null;

        const tenantTimeZone = jwt ? jwt.TenantTimeZoneId : undefined;

        const currentClientId = jwt ? jwt.CurrentTenantId : null;
        this._currentTenantId.next(currentClientId);
        moment.tz.setDefault(tenantTimeZone);
        this._tenantTimeZone.next(tenantTimeZone);

        // setTimeout(_ => {
        this._jwt.next(jwt);
        // });
      })
    );

    this._subscriptions.push(
      this._jwt.subscribe((jwt) => {
        clearTimeout(this._sessionTimeout);

        if (!jwt) {
          return;
        }

        // one minute before expiration
        const ms = (jwt.exp - 60) * 1000;
        const sessionEndNotificationTimeInMilliseconds =
          ms - new Date().getTime();

        this._sessionTimeout = setTimeout(() => {
          // publish notification
        }, sessionEndNotificationTimeInMilliseconds);
      })
    );

    this._storageListener = (event: StorageEvent) => {
      if (event.key === LOCAL_STORAGE_KEY.JWT) {
        if (this.isLoggedIn && event.newValue !== this.tokenValue) {
          window.location.reload();
        }
      }
    };
    window.addEventListener("storage", this._storageListener);
  }

  public hasRight(actions: UserAction[]): boolean {
    if (!this.jwt?.value?.Actions) {
      return false;
    }

    if (!Array.isArray(this.jwt.value.Actions)) {
      return false;
    }

    return actions.some((x) => this.jwt.value.Actions.indexOf(x) >= 0);
  }

  public hasRights(actions: UserAction[]): boolean {
    if (!this.jwt?.value?.Actions) {
      return false;
    }

    if (!Array.isArray(this.jwt.value.Actions)) {
      return false;
    }

    return actions.every((x) => this.jwt.value.Actions.indexOf(x) >= 0);
  }

  public hasModule(module: LxmAppModule): boolean {
    if (!this.jwt?.value?.Modules) {
      return false;
    }

    return this.jwt.value.Modules.indexOf(module) >= 0;
  }

  public isUserOwner(userId: string) {
    if (!userId) return false;
    const jwt = this.jwt.value;
    const nameid = jwt?.nameid;
    if (!nameid) return false;
    return nameid === userId;
  }

  public get tokenValue(): string {
    return this._tokenString.value;
  }

  public get currentTenantId(): BehaviorSubject<string> {
    return this._currentTenantId;
  }

  public get tenantTimeZone(): BehaviorSubject<string> {
    return this._tenantTimeZone;
  }

  public get jwt(): BehaviorSubject<IToken> {
    return this._jwt;
  }

  public loginWithPassword(username: string, password: string) {
    const svc = this;
    const sessionId = Guid.newGuid().toString();
    return this._http
      .post<string>(`api/auth/password/${sessionId}`, { username, password })
      .pipe(
        switchMap(async (res) => {
          await this._setAuthCookie(sessionId);
          svc._tokenString.next(res);
          return res;
        })
      );
  }

  public initMobileIdAuth(phoneNumber: string) {
    return this._http.post<any>("api/mobileId/initAuth", { phoneNumber });
  }

  public loginWithMobileId(sessionId: any) {
    const svc = this;
    return this._http.get<any>(`api/mobileId/authStatus/${sessionId}`).pipe(
      switchMap(async (res) => {
        await this._setAuthCookie(sessionId);
        svc._tokenString.next(res);
        return res;
      })
    );
  }

  private async _setAuthCookie(sessionId: string) {
    await this._http.get<any>(`api/auth/finalize/${sessionId}`).toPromise();
    // return this._updateAuthCookie(`api/auth/finalize/${sessionId}`);
  }

  private async _clearAuthCookie() {
    return this._http.get<any>("api/auth/logout");
    // return this._updateAuthCookie('api/auth/logout');
  }

  private _updateAuthCookie(url: string) {
    return new Promise<void>((resolve) => {
      const img = document.createElement("img");

      img.onload = function () {
        img.remove();
        resolve();
      };

      img.onerror = function () {
        img.remove();
        resolve();
      };

      img.src = environment.apiUrl + url;
      document.body.append(img);
    });
  }

  public initIdCardAuth() {
    return this._http.post<any>("api/idCard/initAuth", {});
  }

  public loginWithIdCard(sessionId: string) {
    const svc = this;
    return this._http.get<string>(`api/idcard/authStatus/${sessionId}`).pipe(
      switchMap(async (res) => {
        await this._setAuthCookie(sessionId);
        svc._tokenString.next(res);
        return res;
      })
    );
  }

  public initAzureAdAuth() {
    return this._http.get<any>("api/azureAd/initAuth");
  }

  public loginWithAzureAd(sessionId: string) {
    const svc = this;
    return this._http.get<string>(`api/azureAd/authStatus/${sessionId}`).pipe(
      switchMap(async (res) => {
        await this._setAuthCookie(sessionId);
        svc._tokenString.next(res);
        return res;
      })
    );
  }

  public async logout() {
    this._dialog.closeAll();
    this._tokenString.next("");
    await this._clearAuthCookie();
  }

  public logoutOnPageUnload() {
    this._beforeUnloadListener = () => {
      this.logout();
    };
    window.addEventListener("beforeunload", this._beforeUnloadListener);
  }

  public getUserSettings() {
    return this._http.get<IUserSettings>(`api/user/settings`);
  }

  public switchTenant(tenantId: string) {
    const svc = this;
    const sessionId = Guid.newGuid().toString();
    this._http
      .post<any>(`api/auth/switchTenant/${tenantId}/${sessionId}`, {})
      .pipe(
        switchMap(async (res) => {
          await this._setAuthCookie(sessionId);
          return res;
        })
      )
      .subscribe((res) => {
        svc._tokenString.next(res);
        window.location.replace("/");
      });
  }

  public navigateWithReturnUrl(
    url: string,
    returnUrl = document.location.pathname == url
      ? undefined
      : document.location.pathname + document.location.search
  ) {
    this._router
      .navigate([url], {
        queryParams: { returnUrl: this._normalizeReturnUrl(returnUrl) },
        queryParamsHandling: "merge"
      })
      .then((res) => {})
      .catch((err) => {});
  }

  public getRedirectTargetUrl(): string {
    const q = this._router.parseUrl(this._router.url).queryParams["returnUrl"];
    const raw = Array.isArray(q) ? q[0] : q;
    const decoded = raw ? decodeURI(raw) : "";
    return this._normalizeReturnUrl(decoded || "/");
  }

  private _normalizeReturnUrl(returnUrl: string): string {
    if (!returnUrl) {
      return "/";
    }

    // Check if returnUrl is an absolute URL
    try {
      const url = new URL(returnUrl);
      return url.pathname + url.search;
    } catch (e) {
      return returnUrl;
    }
  }

  public authToken(token: string, returnUrl: string) {
    const svc = this;
    const sessionId = Guid.newGuid().toString();
    this._http
      .post<any>(`api/auth/token/${sessionId}`, { token: token })
      .pipe(
        switchMap(async (res) => {
          if (res !== null) {
            await this._setAuthCookie(sessionId);
          }
          return res;
        })
      )
      .subscribe(
        (res) => {
          if (res !== null) {
            svc._tokenString.next(res);
          }
          const normalizedUrl = this._normalizeReturnUrl(returnUrl);
          this._router.navigateByUrl(normalizedUrl);
        },
        (x: HttpBadRequestResponse) => {
          this._handleAuthError(x);
        }
      );
  }

  public authInvitationToken(token: string, returnUrl: string) {
    const svc = this;
    const sessionId = Guid.newGuid().toString();
    this._http
      .post<any>(`api/auth/invitation/${sessionId}`, { token: token })
      .pipe(
        switchMap(async (res) => {
          if (res !== null) {
            await this._setAuthCookie(sessionId);
          }
          return res;
        })
      )
      .subscribe(
        (res) => {
          if (res !== null) {
            svc._tokenString.next(res);
          }
          const normalizedUrl = this._normalizeReturnUrl(returnUrl);
          this._router.navigateByUrl(normalizedUrl);
        },
        (x: HttpBadRequestResponse) => {
          this._handleAuthError(x);
        }
      );
  }

  public verifyTenantInvitationToken(token: string, returnUrl: string) {
    const svc = this;
    const sessionId = Guid.newGuid().toString();
    this._http
      .post<any>(`api/auth/tenantInvitation/${sessionId}`, { token: token })
      .pipe(
        switchMap(async (res) => {
          if (res !== null) {
            await this._setAuthCookie(sessionId);
          }
          return res;
        })
      )
      .subscribe(
        (res) => {
          if (res !== null) {
            svc._tokenString.next(res);
          }
          const normalizedUrl = this._normalizeReturnUrl(returnUrl);
          this._router.navigateByUrl(normalizedUrl);
        },
        (x: HttpBadRequestResponse) => {
          this._handleAuthError(x);
        }
      );
  }

  private _parseJwt(tokenString: string): IToken {
    var token = JSON.parse(tokenString, function (key: string, value: any) {
      if (key === "TenantFeatures") {
        return parseInt(value);
      }
      return value;
    });

    if (token) {
      token["Actions"] = token["Actions_v2"];
      delete token["Actions_v2"];
    }

    return token;
  }

  public get isLoggedIn() {
    return (
      this.tokenValue !== undefined &&
      this.tokenValue !== null &&
      this.tokenValue !== ""
    );
  }

  private _handleAuthError(error: HttpBadRequestResponse) {
    this._lastError = error;
    this._dialog.closeAll();
    this._router.navigate(["/login"]);
  }

  ngOnDestroy() {
    this._subscriptions.forEach((sub) => sub.unsubscribe());

    if (this._sessionTimeout) {
      clearTimeout(this._sessionTimeout);
    }

    if (this._storageListener) {
      window.removeEventListener("storage", this._storageListener);
    }
    if (this._beforeUnloadListener) {
      window.removeEventListener("beforeunload", this._beforeUnloadListener);
    }

    if (this.shouldLogOutSubscription) {
      this.shouldLogOutSubscription.unsubscribe();
    }
  }
}

interface IToken {
  nameid: string;
  unique_name: string;
  given_name: string;
  email: string;
  CurrentTenantId: string;
  // TenantType: TenantType;
  // TenantFeatures: TenantFeature;

  TenantTimeZoneId: string;
  Actions: UserAction[];
  Modules: LxmAppModule[];
  Visitor: any;
  nbf: number;
  exp: number;
  iat: number;
}
